import React from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RefreshCw, Volume2, VolumeX, Zap, RotateCcw, Calendar } from 'lucide-react';
import { BranchSelector } from '@/components/BranchSelector';
import { CreateManualOrderDialog } from '@/components/CreateManualOrderDialog';

interface OrdersHeaderProps {
    isFastMode: boolean;
    setIsFastMode: (value: boolean) => void;
    autoRefresh: boolean;
    setAutoRefresh: (value: boolean) => void;
    soundEnabled: boolean;
    setSoundEnabled: (value: boolean) => void;
    onRefresh: () => void;
    isRefetching: boolean;
    selectedBranchId: string | null;
    currentBranch: any;
    dateRange: 'today' | 'yesterday' | 'week' | 'all';
    setDateRange: (range: 'today' | 'yesterday' | 'week' | 'all') => void;
}

export const OrdersHeader: React.FC<OrdersHeaderProps> = ({
    isFastMode,
    setIsFastMode,
    autoRefresh,
    setAutoRefresh,
    soundEnabled,
    setSoundEnabled,
    onRefresh,
    isRefetching,
    selectedBranchId,
    currentBranch,
    dateRange,
    setDateRange
}) => {
    return (
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className="flex flex-col gap-2 w-full lg:w-auto">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Order Management</h1>
                    <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
                        <Switch
                            id="fast-mode"
                            checked={isFastMode}
                            onCheckedChange={setIsFastMode}
                            className="data-[state=checked]:bg-blue-600"
                        />
                        <Label htmlFor="fast-mode" className="text-sm font-medium cursor-pointer flex items-center gap-1 pr-2">
                            <Zap className={`h-3 w-3 ${isFastMode ? 'text-blue-600 fill-blue-600' : 'text-gray-500'}`} />
                            Fast Mode
                        </Label>
                    </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="hidden sm:inline">Manage incoming orders and track status</span>
                    <BranchSelector />
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                <Select value={dateRange} onValueChange={setDateRange}>
                    <SelectTrigger className="w-[140px]">
                        <Calendar className="h-4 w-4 mr-2" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="yesterday">Yesterday</SelectItem>
                        <SelectItem value="week">This Week</SelectItem>
                        <SelectItem value="all">All Time</SelectItem>
                    </SelectContent>
                </Select>

                {selectedBranchId && (
                    <CreateManualOrderDialog
                        branchId={selectedBranchId}
                        currency={currentBranch?.currency}
                        onOrderCreated={onRefresh}
                    />
                )}

                <Button
                    variant="outline"
                    size="sm"
                    onClick={onRefresh}
                    disabled={isRefetching}
                    className={`relative ${isRefetching ? 'opacity-70' : ''}`}
                >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>

                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    className={autoRefresh ? 'text-blue-600 bg-blue-50' : 'text-gray-400'}
                    title={autoRefresh ? 'Auto-Refresh On' : 'Auto-Refresh Off'}
                >
                    <RotateCcw className={`h-5 w-5 ${autoRefresh ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
                </Button>

                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={soundEnabled ? 'text-blue-600 bg-blue-50' : 'text-gray-400'}
                    title={soundEnabled ? 'Sound Notifications On' : 'Sound Notifications Off'}
                >
                    {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
                </Button>
            </div>
        </div>
    );
};
